import React, { type PropsWithChildren, useRef } from "react";
import cx from "classnames";
import useLockScroll from "@/hooks/useLockScroll";
import { NavLogo } from "../NavLogo";
import { Button, CloseIcon, Separator, Typography } from "@picklebet/ui";
import { BackButton } from "../BackButton";
import { useLocation } from "@gatsbyjs/reach-router";
import { useIsMobile } from "@/hooks/useDisplay";

import * as styles from "./FullScreenModal.module.scss";

type FullScreenModalProps = PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  withBackButton?: boolean;
  closeButtonComponent?: React.ReactNode;
  hideHeader?: boolean;
  hideCloseButton?: boolean;
  hideSeparator?: boolean;
  includeLogoTextOnMobile?: boolean;
  /**
   * title appears inside the header on mobile
   */
  moveTitleOnMobile?: boolean;
  style?: React.CSSProperties;
  subTitle?: React.ReactNode;
  /**
   * custom back button inside the modal
   */
  onBackClick?: () => void;
  backAnimatePresence?: boolean;
}>;

const FullScreenModal = ({
  isOpen,
  onClose,
  title,
  className,
  children,
  contentClassName,
  withBackButton = false,
  hideHeader = false,
  hideCloseButton = false,
  hideSeparator = false,
  includeLogoTextOnMobile = false,
  moveTitleOnMobile = false,
  style,
  subTitle,
  onBackClick,
  backAnimatePresence,
}: FullScreenModalProps): JSX.Element => {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const initialPath = useRef(pathname);
  const showTitleInHeader = moveTitleOnMobile && isMobile;

  useLockScroll(isOpen);
  return (
    <>
      {isOpen && (
        <div
          className={cx(styles.modal, className)}
          style={style}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          {!hideHeader && (
            <div className={styles.header}>
              <div className={styles.container}>
                <div className={styles.headerContent}>
                  <div className={styles.headerLeft}>
                    {withBackButton && (
                      <BackButton
                        animatePresence={
                          onBackClick
                            ? backAnimatePresence
                            : pathname !== initialPath.current
                        }
                        className={styles.positionBackButton}
                        onClick={onBackClick}
                      />
                    )}
                    <NavLogo includeTextOnMobile={includeLogoTextOnMobile} />
                  </div>
                  {showTitleInHeader && (
                    <div className={styles.headerCentered}>{title}</div>
                  )}
                  {!hideCloseButton && (
                    <Button
                      className={styles.close}
                      onClick={() => onClose()}
                      variant={"link"}
                    >
                      <CloseIcon />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={cx(styles.content, contentClassName)}>
            {title && !showTitleInHeader ? (
              <>
                <Typography
                  variant={"h1"}
                  className={cx({ [styles.withSubtitle]: !!subTitle })}
                >
                  {title}
                </Typography>
                {subTitle && (
                  <Typography variant={`body`} noSpacing>
                    {subTitle}
                  </Typography>
                )}
                {!hideSeparator && <Separator />}
              </>
            ) : null}
            {children}
          </div>
        </div>
      )}
    </>
  );
};

FullScreenModal.displayName = "FullScreenModal";

export default FullScreenModal;
